import React, { FormEvent } from 'react';
import { Loader2, X } from 'lucide-react';
import { Message } from '../../types';

export type ImprovementKind = 'client' | 'bot' | string | null;

export type ConversationsImprovementModalProps = {
  open: boolean;
  improvementKind: ImprovementKind;
  improvementMessage: Message | null;
  improvementText: string;
  setImprovementText: (value: string) => void;
  improvementError: string | null;
  improvementSuccess: string | null;
  isImprovementSending: boolean;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
};

export default function ConversationsImprovementModal({
  open,
  improvementKind,
  improvementMessage,
  improvementText,
  setImprovementText,
  improvementError,
  improvementSuccess,
  isImprovementSending,
  onClose,
  onSubmit,
}: ConversationsImprovementModalProps) {
  if (!open) return null;

  const isClient = improvementKind === 'client';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {isClient
                ? 'Ensinar resposta da IA'
                : 'Corrigir/Melhorar resposta da IA'}
            </h3>
            <p className="text-sm text-slate-500">
              {isClient
                ? 'Descreva como a IA deveria responder essa mensagem do cliente.'
                : 'Explique como essa resposta poderia ser melhorada ou corrigida. Isso vira uma regra de atendimento para melhorar futuras mensagens.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
            aria-label="Fechar modal"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              {isClient ? 'Mensagem do cliente' : 'Mensagem da IA'}
            </div>
            <p className="text-sm text-slate-800 whitespace-pre-line">
              {improvementMessage?.content || ''}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">
              {isClient
                ? 'Como a IA deveria responder essa mensagem?'
                : 'Como você corrigiria ou melhoraria essa resposta?'}
            </label>
            <textarea
              className="min-h-[140px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200"
              placeholder={isClient
                ? 'Ex.: responda com cordialidade, confirme detalhes, ofereça próximos passos...'
                : 'Ex.: seja mais direto, peça uma informação antes de responder, ajuste o tom...'}
              value={improvementText}
              onChange={(event) => setImprovementText(event.target.value)}
              disabled={isImprovementSending}
            />
            <div className="text-xs text-slate-400">
              Seja específico: descreva como a IA deveria responder de forma mais útil e clara.
            </div>
          </div>

          {improvementError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {improvementError}
            </div>
          )}
          {improvementSuccess && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {improvementSuccess}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 transition"
              disabled={isImprovementSending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isImprovementSending || !improvementText.trim()}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-4 text-sm font-semibold text-white shadow-lg shadow-[#2f7d72]/25 transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isImprovementSending ? <Loader2 size={16} className="animate-spin" /> : null}
              {isClient ? 'Enviar resposta' : 'Enviar correção'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
